import { PolicyLicenseWarning } from './PolicyLicenseWarning';
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIconSlot,
  DropdownMenuTrigger,
  Typography,
} from '@eye-able/ui';
import { Archive, LayoutGrid, Plus, PlusIcon, Trash2 } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import ConfirmDialog from '@components/ConfirmDialog';
import LoadingSpinner from '@components/LoadingSpinner';
import {
  useFetchPolicies,
  type PolicyResponse,
} from '@http/queries/useFetchPolicies';
import { useDeletePolicy } from '@http/queries/useDeletePolicy';
import { useArchivePolicy } from '@http/queries/useArchivePolicy';
import { PolicyInventoryCard } from './PolicyInventoryCard';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useGovernanceAccess } from '@/hooks/useGovernanceAccess';
import { usePolicyEditAccess } from './usePolicyEditAccess';
import { ReadOnlyBanner } from '@/components/access/ReadOnlyBanner';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';
import { CustomPolicyHeader } from './CustomPolicyHeader';
import { CustomPolicyTabs } from './CustomPolicyTabs';
import { usePolicyDomainOptions } from './usePolicyDomainOptions';
import { T } from '@/i18n/T';

function PoliciesContent() {
  const { t } = useInlineTranslation();
  const { canEdit } = useGovernanceAccess();
  const { canEditPolicy } = usePolicyEditAccess();
  const { data: policies, isLoading, isError } = useFetchPolicies();
  const deletePolicy = useDeletePolicy();
  const archivePolicy = useArchivePolicy();
  const [policyToDelete, setPolicyToDelete] = useState<PolicyResponse | null>(
    null,
  );
  const [policyToArchive, setPolicyToArchive] = useState<PolicyResponse | null>(
    null,
  );
  const handleConfirmDelete = () => {
    if (!canEdit || !policyToDelete || !canEditPolicy(policyToDelete)) return;
    deletePolicy.mutate(policyToDelete.id, {
      onSuccess: () => setPolicyToDelete(null),
    });
  };

  const handleConfirmArchive = () => {
    if (!canEdit || !policyToArchive || !canEditPolicy(policyToArchive)) {
      return;
    }
    archivePolicy.mutate(policyToArchive, {
      onSuccess: () => setPolicyToArchive(null),
    });
  };

  // Archived policies live on their own page (/policies/archived).
  const activePolicies = (policies ?? []).filter(
    (policy) => policy.status !== 'archived',
  );
  const domainOptions = usePolicyDomainOptions(activePolicies);

  return (
    <div className='eye:flex eye:flex-col eye:gap-m'>
      <div>
        <section className='eye:flex eye:flex-col eye:gap-s eye:lg:flex-row eye:lg:items-start eye:lg:justify-between'>
          <div>
            <CustomPolicyHeader />
          </div>

          <div className='eye:flex eye:flex-nowrap eye:items-center eye:gap-xs eye:self-start'>
            <Button asChild variant='outline' className='eye:shrink-0'>
              <RouterLink to='/policies/archived'>
                <Archive aria-hidden />
                {t('archivedPolicies')}
              </RouterLink>
            </Button>
            {canEdit ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className='eye:shrink-0'>
                    <Plus aria-hidden />
                    {t('newPolicy')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='eye:min-w-[260px]'>
                  <DropdownMenuItem render={<RouterLink to='/policies/new' />}>
                    <DropdownMenuItemIconSlot>
                      <PlusIcon aria-hidden />
                    </DropdownMenuItemIconSlot>
                    {t('fromScratch')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    render={<RouterLink to='/policies/templates' />}
                  >
                    <DropdownMenuItemIconSlot>
                      <LayoutGrid aria-hidden />
                    </DropdownMenuItemIconSlot>
                    <div className='eye:flex eye:flex-col'>
                      {t('usingTemplateLibrary')}
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </section>

        <div className='eye:mt-s'>
          <CustomPolicyTabs active='policies' />
        </div>
      </div>

      <ReadOnlyBanner show={!canEdit} />

      {!isLoading && !isError ? (
        <PolicyLicenseWarning policies={activePolicies} />
      ) : null}

      <section className='eye:flex eye:flex-col eye:gap-s'>
        {isLoading ? (
          <LoadingSpinner />
        ) : isError ? (
          <Card>
            <CardContent className='eye:p-s'>
              <Typography className='eye:text-grey-120'>
                <T k='couldNotLoadPolicies' />
              </Typography>
            </CardContent>
          </Card>
        ) : activePolicies.length === 0 ? (
          <Card>
            <CardContent className='eye:flex eye:flex-col eye:items-start eye:gap-xs eye:p-s'>
              <Typography tag='h2' variant='headline-s'>
                <T k='noPoliciesYet' />
              </Typography>
              <Typography className='eye:text-grey-120'>
                <T k='noPoliciesDescription' />
              </Typography>
              {canEdit ? (
                <Button asChild variant='yellow' className='eye:mt-3xs'>
                  <RouterLink to='/policies/new'>
                    <Plus aria-hidden />
                    {t('newPolicy')}
                  </RouterLink>
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <div
            id='policies-inventory'
            className='eye:grid eye:gap-s eye:md:grid-cols-2'
          >
            {activePolicies.map((policy) => (
              <PolicyInventoryCard
                key={policy.id}
                policy={policy}
                onDelete={setPolicyToDelete}
                onArchive={setPolicyToArchive}
                canEdit={canEditPolicy(policy)}
                domainOptions={domainOptions}
              />
            ))}
          </div>
        )}
      </section>

      {canEdit ? (
        <ConfirmDialog
          open={policyToDelete !== null}
          onOpenChange={(open) => {
            if (!open) setPolicyToDelete(null);
          }}
          title={t('deletePolicy')}
          description={
            policyToDelete
              ? t('deletePolicyConfirmation', {
                  policyName: policyToDelete.name,
                })
              : undefined
          }
          confirmText={t('delete')}
          confirmIcon={<Trash2 aria-hidden />}
          loading={deletePolicy.isPending}
          onConfirm={handleConfirmDelete}
        />
      ) : null}

      {canEdit ? (
        <ConfirmDialog
          open={policyToArchive !== null}
          onOpenChange={(open) => {
            if (!open) setPolicyToArchive(null);
          }}
          title={t('archivePolicy')}
          description={
            policyToArchive
              ? t('archivePolicyConfirmation', {
                  policyName: policyToArchive.name,
                })
              : undefined
          }
          confirmText={t('archive')}
          confirmIcon={<Archive aria-hidden />}
          loading={archivePolicy.isPending}
          onConfirm={handleConfirmArchive}
        />
      ) : null}
    </div>
  );
}

function Policies() {
  return (
    <GovernanceAccessBoundary>
      <PoliciesContent />
    </GovernanceAccessBoundary>
  );
}

export default Policies;
